import React from 'react';
import { StyleProp, TextStyle, View } from 'react-native';
import MyAppText from './MyAppText';
import TimePicker from 'react-time-picker';
import { UnistylesRuntime } from 'react-native-unistyles';
import DatePicker from 'react-date-picker';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import '../styles/date-picker.css';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { translate } from '../services/translate';
import inputStylesheet from '../styles/inputStyles';

type ValuePiece = Date | null;
export type CalendarValue = ValuePiece;
type MyDateTimePickerProps = {
    label: string;
    editable?: boolean;
    maxDate?: Date;
    minDate?: Date;
    disabled?: boolean;
    zIndex?: number;
    labelFontSize?: number;
    onCalendarOpen?: () => void;
    onCalendarClose?: () => void;
    required?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    viewStyle?: StyleProp<TextStyle>;
} & ({
    form?: undefined;
    dateFieldName?: undefined;
    timeFieldName?: undefined;
    customValidations?: undefined;
    linkedFields?: undefined;
    date: CalendarValue;
    onChangeDate: (date: CalendarValue) => void;
    time: string;
    onChangeTime: (value: string) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form?: UseFormReturn<any, any, any>;
    dateFieldName: string;
    timeFieldName: string;
    date?: undefined;
    onChangeDate?: undefined;
    time?: undefined;
    onChangeTime?: undefined;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any, form: UseFormReturn<any, any, any>) => string | true;
    };
    linkedFields?: string[];
});

export default function DateTimePicker({
    label,
    date,
    time,
    onChangeDate,
    onChangeTime,
    minDate,
    maxDate,
    disabled,
    zIndex,
    labelFontSize,
    onCalendarOpen,
    onCalendarClose,
    required,
    labelStyle,
    viewStyle,
    form,
    dateFieldName,
    timeFieldName,
    customValidations,
    linkedFields
}: MyDateTimePickerProps) {
    const { styles, theme } = useStyles(styleSheet);
    const localMinDate = minDate ? minDate : new Date(1900, 0, 1);

    if (form && dateFieldName && timeFieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        if (customValidations) {
            rules.validate = Object.fromEntries(
                Object.entries(customValidations).map(([key, fn]) => [
                    key,
                    (value: unknown) => fn(value, form)
                ])
            );
        }

        return (
            <View style={[viewStyle ?? { rowGap: 5, flex: 1 }, zIndex ? { zIndex } : {}]}>
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    <MyAppText
                        style={labelStyle ?? styles.label(labelFontSize)}>
                        {label}
                    </MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <View style={{ flexDirection: 'row', columnGap: 5, flex: 1 }}>

                    <Controller
                        control={form.control}
                        name={dateFieldName}
                        rules={rules}
                        render={({ field, fieldState }) => (
                            <View style={[styles.container({}), { flexGrow: 2 }]}>
                                <DatePicker
                                    calendarProps={{ locale: 'pt-BR' }}
                                    maxDate={maxDate}
                                    minDate={localMinDate}
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    onChange={(val) => {
                                        field.onChange(val);
                                        if (linkedFields?.length) {
                                            form.trigger(linkedFields);
                                        }
                                    }}
                                    autoFocus={false}
                                    shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                                    onCalendarOpen={onCalendarOpen}
                                    onCalendarClose={onCalendarClose}
                                    value={field.value}
                                    clearIcon={null}
                                    locale='pt-BR'
                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight${disabled ? 'Disabled' : ''}`] : ['inputColor', `inputColorDark${disabled ? 'Disabled' : ''}`]}
                                    calendarIcon={<FontAwesomeIcon icon={faCalendar as IconDefinition} color={theme.colors.neutral['400']}></FontAwesomeIcon>}
                                    disabled={disabled}
                                />
                                {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                            </View>
                        )}
                    />
                </View>
                <View style={{ flexGrow: 1, maxWidth: 65 }}>
                    <Controller
                        control={form.control}
                        name={timeFieldName}
                        rules={rules}
                        render={({ field }) => (
                            <View style={[styles.container({})]}>
                                <TimePicker
                                    disableClock={true}
                                    format='HH:mm'
                                    locale='pt-BR'
                                    maxDetail='minute'
                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight${disabled ? 'Disabled' : ''}`] : ['inputColor', `inputColorDark${disabled ? 'Disabled' : ''}`]}
                                    value={field.value}
                                    clearIcon={null}
                                    disabled={disabled}
                                    onChange={(value) => {
                                        if (value) {
                                            field.onChange(value);
                                            if (linkedFields?.length) {
                                                form.trigger(linkedFields);
                                            }
                                        }
                                    }}
                                />
                            </View>
                        )}
                    />
                </View>
            </View>
        );
    } else if (date !== undefined && onChangeDate !== undefined && time !== undefined && onChangeTime !== undefined) {
        return (
            <View style={[viewStyle ?? { rowGap: 5, flex: 1 }, zIndex ? { zIndex } : {}]}>
                <View style={{ flexDirection: 'row', gap: 3 }}>
                    <MyAppText
                        style={labelStyle ?? styles.label(labelFontSize)}>
                        {label}
                    </MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <View style={{ flexDirection: 'row', columnGap: 5, flex: 1 }}>
                    <View style={[styles.container({}), { flexGrow: 2 }]}>
                        <DatePicker
                            calendarProps={{ locale: 'pt-BR' }}
                            maxDate={maxDate}
                            minDate={localMinDate}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            onChange={onChangeDate}
                            autoFocus={false}
                            shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                            onCalendarOpen={onCalendarOpen}
                            onCalendarClose={onCalendarClose}
                            value={date}
                            clearIcon={null}
                            locale='pt-BR'
                            className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight${disabled ? 'Disabled' : ''}`] : ['inputColor', `inputColorDark${disabled ? 'Disabled' : ''}`]}
                            calendarIcon={<FontAwesomeIcon icon={faCalendar as IconDefinition} color={theme.colors.neutral['400']}></FontAwesomeIcon>}
                            disabled={disabled}
                        />
                    </View>
                    <View style={[styles.container({}), { flexGrow: 1, maxWidth: 65 }]}>
                        <TimePicker
                            disableClock={true}
                            format='HH:mm'
                            locale='pt-BR'
                            maxDetail='minute'
                            className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight${disabled ? 'Disabled' : ''}`] : ['inputColor', `inputColorDark${disabled ? 'Disabled' : ''}`]}
                            value={time}
                            clearIcon={null}
                            disabled={disabled}
                            onChange={(value) => {
                                if (value) {
                                    onChangeTime(value);
                                }
                            }}
                        />
                    </View>
                </View>
            </View>
        );
    }
}


const styleSheet = createStyleSheet((theme) => ({
    label: (fontSize) => ({
        color: theme.colors.labelColor,
        fontSize,
    }),
    container: ({ zIndex }: { zIndex?: number; }) => {
        const { styles: defaultInputStyle } = useStyles(inputStylesheet);
        const placeholder = defaultInputStyle.input({ placeholder: true });
        const defaultInput = defaultInputStyle.input({});
        const invalid = defaultInputStyle.input({ invalid: true });

        return {
            zIndex,
            '--daterange-placeholder-color': placeholder.color,
            '--daterange-invalid-border-color': invalid.borderColor,
            '--daterange-text-color': defaultInput.color,
            '--daterange-border-color': defaultInput.borderColor,
            '--daterange-button-color': defaultInput.borderColor,
            '--daterange-border-radius': '6px',
            '--daterange-font-size': '14px',
            '--daterange-font-weight': '500',

            '--daterange-calendar-background-color': theme.colors.calendar.background,
            '--daterange-calendar-border-color': theme.colors.calendar.border,
            '--daterange-calendar-days-text-color': theme.colors.calendar.dayText,
            '--daterange-calendar-navigation-text-color': theme.colors.calendar.navigationText,
            '--daterange-calendar-navigation-text-disabled-color': theme.colors.calendar.navigationDisabledText,
            '--daterange-calendar-navigation-background-hover-color': theme.colors.calendar.navigationBackgroundHover,
            '--daterange-calendar-days-background-selected-active-color': theme.colors.calendar.dayBackgroundSelectedActive,
            '--daterange-calendar-days-background-selected-range-color': theme.colors.calendar.dayBackgroundSelectedRange,
            '--daterange-calendar-days-text-selected-range-color': theme.colors.calendar.dayTextSelectedRangeColor,
            '--daterange-calendar-days-text-disabled-color': theme.colors.calendar.dayTextDisabledColor,
        };
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
}));
